import React, { createContext, useContext, useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { UserInviteStatus, UserRole } from '../models/user';
import { useOrganization } from './organization';

interface Member {
  email: string;
  role: UserRole;
  status: UserInviteStatus;
  date_joined?: string;
}

interface MembersContextType {
  members: Member[];
  isLoading: boolean;
  setMembers: React.Dispatch<React.SetStateAction<Member[]>>;
}

const MembersContext = createContext<MembersContextType | undefined>(undefined);

export const MembersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { getAccessTokenSilently } = useAuth0();
  const { organization } = useOrganization();

  useEffect(() => {
    const fetchMembers = async () => {
      if (!organization) return;
      setIsLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/organization/members`, { headers: { 'Authorization': token } });
        setMembers(response.data.members);
      } catch (error) {
        toast.error((error as AxiosError).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, [organization, getAccessTokenSilently]);

  return (
    <MembersContext.Provider value={{ members, isLoading, setMembers }}>
      {children}
    </MembersContext.Provider>
  );
};

export const useMembers = () => {
  const context = useContext(MembersContext);
  if (!context) {
    throw new Error('useMembers must be used within a MembersProvider');
  }
  return context;
};
